import React, { useState, useEffect, useContext } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material"
import { GridExpandMoreIcon } from "@mui/x-data-grid"
import { Controller, useForm } from "react-hook-form"
import { capitalize, debounce } from "lodash"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation } from "react-query"
import { DateTime } from "luxon"
import { useSnackbar } from "notistack"
import { EventBusy, Vignette } from "@mui/icons-material"
import CustomeDatePicker from "../../Component/datePicker.jsx"
import useAxiosPrivate from "../../Hooks/useAxiosPrivate.js"
import AuthContext from "../../Context/auth_provider.jsx"

function CreatePreRideProgram() {
    const {
        handleSubmit,
        setValue,
        formState: { errors },
        watch,
        control,
    } = useForm({
        defaultValues: {
            spending_limit_duration: "daily",
            trip_limit_duration: "daily",
            time_limit_timezon: "ETH (LOCAL)",
            date_limit: false,
            spending_limit: false,
            time_limit: false,
            trip_limit: false,
            has_expire_date: false,
        },
    })

    const [customeWatch, setCustomeWatch] = useState(null)
    const [expandedAccord, setExpandedAccord] = useState(false)

    // NONE UI STATES
    const { tId } = useParams()
    const { companyId, teamId } = useContext(AuthContext)
    const domain = teamId ? `/team/${teamId}` : `/corporate/${companyId}`
    const endPoint = tId ? `teams/${tId}/preride` : "preride"
    const navigate = useNavigate()
    const programRoute = tId ? `/departments/${tId}/pre-RIDE` : "/pre-RIDE"

    useEffect(() => {
        const subscription = watch((d) => {
            const tripLim = d.trip_limit_amount
            const spendingLim = d.spending_limit_amount

            // TO DEBOUNCE THE WATCH, AVOIDS RE-RENDER ON EVERY KEYSTROKE
            if (tripLim >= 0 || spendingLim >= 0)
                debounce(() => setCustomeWatch(d), 100)()
        })

        return () => subscription.unsubscribe()
    }, [watch])

    // SERVER STATE AND MUTATIONS
    const axiosPrivate = useAxiosPrivate()
    const { enqueueSnackbar } = useSnackbar()

    const { mutate, isLoading } = useMutation(
        (data) =>
            axiosPrivate
                .post(`${domain}/${endPoint}`, data, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                })
                .then((res) => res.data),
        {
            onSuccess: () => {
                enqueueSnackbar("Success", {
                    variant: "success",
                })
                navigate(programRoute)
            },
            onError: (error) => {
                const fieldError =
                    error.response?.data?.error?.field_error?.[0]?.description
                const errorMessage = error.response?.data?.error?.message

                enqueueSnackbar(
                    fieldError || errorMessage || "Request Failed",
                    { variant: "error" }
                )
            },
        }
    )

    // HANDLERS
    const createProgram = (d) => {
        // BACKEND DOESN'T RECEIVE A `NONE DATEtIME` VALUE FOR EXPIRE DATE
        if (!d.expire_date) {
            const D = {
                ...d,
            }
            mutate({
                ...D,
            })
        } else {
            mutate(d)
        }
    }

    return (
        <Container
            maxWidth="false"
            sx={{ pb: { md: "3em", xs: "1em" }, pt: { md: "3em" } }}
        >
            <Grid
                container
                spacing={1}
                justifyContent="space-around"
                sx={{
                    pt: "1.5em",
                    pl: { xs: 0 },
                }}
            >
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={4}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1em",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: ".5em",
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: "500" }}>
                            Name
                        </Typography>
                        <Controller
                            name="name"
                            rules={{ required: "Name is required" }}
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                                <TextField
                                    value={value}
                                    inputRef={ref}
                                    onChange={(e) =>
                                        onChange(e.currentTarget.value)
                                    }
                                    fullWidth
                                    helperText={errors.name?.message}
                                />
                            )}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: ".5em",
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: "500" }}>
                            Description
                        </Typography>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    value={value}
                                    onChange={(e) =>
                                        onChange(e.currentTarget.value)
                                    }
                                    multiline
                                    rows={2}
                                    fullWidth
                                />
                            )}
                        />
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: ".5em",
                    }}
                >
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                        Rule
                    </Typography>
                    <Accordion
                        expanded={expandedAccord === "spending_limit"}
                        onChange={() => {
                            if (expandedAccord === "spending_limit") {
                                setExpandedAccord(false)
                            } else {
                                setExpandedAccord("spending_limit")
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <GridExpandMoreIcon color="secondary" />
                            }
                        >
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                }}
                            >
                                <IconButton>
                                    <Vignette />
                                </IconButton>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Spending Limit
                                    </Typography>
                                    <Typography variant="body2">
                                        {customeWatch?.spending_limit_amount &&
                                        watch("spending_limit_duration")
                                            ? `${
                                                  customeWatch?.spending_limit_amount
                                              } ETB ${watch(
                                                  "spending_limit_duration"
                                              )}`
                                            : "No Limit"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                color="secondary"
                                sx={{ fontWeight: "bold", alignSelf: "center" }}
                            >
                                Edit
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="subtitle2">
                                Enter limit amount
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="spending_limit_amount"
                                        control={control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <TextField
                                                fullWidth
                                                value={value || ""}
                                                onChange={(e) => {
                                                    const val =
                                                        e.currentTarget.value
                                                    onChange(Number(val))
                                                    if (val > 0) {
                                                        setValue(
                                                            "spending_limit",
                                                            true
                                                        )
                                                    } else
                                                        setValue(
                                                            "spending_limit",
                                                            false
                                                        )
                                                }}
                                                type="number"
                                                InputProps={{
                                                    inputProps: {
                                                        min: 200,
                                                        sx: {
                                                            backgroundColor:
                                                                "white",
                                                            mr: ".5em",
                                                        },
                                                    },
                                                    sx: {
                                                        "&.MuiInputBase-root": {
                                                            pr: "0.5em",
                                                            backgroundColor:
                                                                "#dfdfdf",
                                                        },
                                                    },
                                                    endAdornment: (
                                                        <Typography
                                                            sx={{
                                                                fontWeight:
                                                                    "bold",
                                                            }}
                                                        >
                                                            ETB
                                                        </Typography>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="spending_limit_duration"
                                        control={control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <Autocomplete
                                                value={capitalize(value)}
                                                onChange={(e, newValue) =>
                                                    onChange(
                                                        newValue.toLowerCase()
                                                    )
                                                }
                                                options={[
                                                    "Daily",
                                                    "Monthly",
                                                    "Weekly",
                                                ]}
                                                renderInput={(params) => (
                                                    <TextField {...params} />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expandedAccord === "expire_date"}
                        onChange={() => {
                            if (expandedAccord === "expire_date") {
                                setExpandedAccord(false)
                            } else {
                                setExpandedAccord("expire_date")
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <GridExpandMoreIcon color="secondary" />
                            }
                        >
                            <Box sx={{ flexGrow: 1, display: "flex" }}>
                                <IconButton>
                                    <EventBusy />
                                </IconButton>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Expire Date
                                    </Typography>
                                    <Typography variant="body2">
                                        {watch("expire_date")
                                            ? DateTime.fromISO(
                                                  watch("expire_date")
                                              ).toFormat("f")
                                            : "No Expire Date"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                color="secondary"
                                sx={{ fontWeight: "bold", alignSelf: "center" }}
                            >
                                Edit
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2">
                                Enter Expire Date
                            </Typography>
                            <Controller
                                name="expire_date"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <CustomeDatePicker
                                        value={value}
                                        onChange={onChange}
                                        minDate={DateTime.now().toISO()}
                                    />
                                )}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid item xs={12} sm={12} md={11}>
                    <Divider sx={{ mx: "auto", my: ".5em", mt: "1.5em" }} />
                </Grid>
                <Grid item xs={12} sm={12} md={4} />
                <Grid item xs={12} sm={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            margin: "auto",
                        }}
                    >
                        <Button
                            color="common"
                            variant="contained"
                            onClick={() => navigate(programRoute)}
                            sx={{
                                marginLeft: "auto",
                                marginRight: "1em",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            startIcon={
                                isLoading ? (
                                    <CircularProgress
                                        size="1em"
                                        sx={{ color: "white" }}
                                    />
                                ) : null
                            }
                            variant="contained"
                            onClick={handleSubmit(createProgram)}
                        >
                            Add
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    )
}

export default CreatePreRideProgram
